/**
 * 공용 드래그·리사이즈 모달 프레임 (2026-06-24).
 *
 *   - 헤더(.dm-drag-handle 영역)를 잡고 이동, 우하단 핸들로 크기 조절.
 *   - 화면 경계 clamp(밖으로 안 사라짐) + 최소 크기 보장.
 *   - 작은 화면(<640px)은 드래그/리사이즈 끄고 기존처럼 전체 중앙 모달.
 *   - 위치는 left/top(absolute)로 — CSS transform 을 쓰지 않아 내부 position:fixed
 *     자식(중첩 팝업)이 어긋나지 않는다.
 *   - 닫기: ×. **바깥(배경) 클릭으로는 닫히지 않는다** (2026-07-29, v2.026).
 *   - ⭐ESC 는 **보기만 하는 창**만 닫는다 — closeOnEsc 를 켠 창만 (v2.231.0 · 사용자 결정 H-4).
 *     기본은 꺼짐: 입력 창이 ESC 한 번에 사라지는 사고를 「켜짐이 기본인 옵션」이 새 창마다 되살려 왔다.
 *     ESC 는 맨 위 창 하나만 받는다(lib/dialog-stack.ts) — 위 창의 ESC 가 아래 창까지 닫던 것을 막는다.
 *     작성 중 실수로 배경을 눌러 입력이 통째로 날아가는 사고가 반복돼 전 모달에서 껐다.
 *     굳이 필요한 창만 closeOnBackdrop 를 켜서 쓴다.
 *
 * 사용: <DraggableModal onClose={..}> ... 헤더 엘리먼트에 className="dm-drag-handle" ... </DraggableModal>
 *   children 안에 자체 헤더(제목·× 버튼)를 두고, 그 헤더에 dm-drag-handle 을 붙이면 그 부분이 드래그 핸들이 된다.
 */
import { useEffect, useRef, useState } from 'react';
import { useDialogLayer } from '../lib/dialog-stack';
import type { ReactNode, PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react';

export function DraggableModal({
  onClose,
  children,
  initialWidth = 920,
  initialHeight,
  minWidth = 360,
  minHeight = 220,
  zIndex = 50,
  closeOnBackdrop = false,
  closeOnEsc = false,
  onEsc,
  dragHandleSelector = '.dm-drag-handle',
  bodyClassName = 'flex-1 overflow-y-auto',
  backdrop = true,
  initialX,
  initialY,
}: {
  onClose: () => void;
  children: ReactNode;
  initialWidth?: number;
  initialHeight?: number;
  minWidth?: number;
  minHeight?: number;
  zIndex?: number;
  /**
   * 배경(어둡게 깔리는 막) — 기본 true. **false 면 창만 떠 있고 뒤 화면을 그대로 쓸 수 있다** (v2.237.0 · 원가분석표 보면서 견적서 쓰기).
   *   떠 있는 창은 zIndex 를 다른 창(기본 50)보다 낮게 주어, 뒤 화면이 여는 창(가져오기·미리보기)이 위로 오게 한다.
   */
  backdrop?: boolean;
  /** 처음 자리(px · 화면 기준) — 없으면 가운데. 화면 밖이면 안으로 당긴다 */
  initialX?: number;
  initialY?: number;
  /** 배경(바깥) 클릭으로 닫기. 기본 false — 작성 중 오클릭 사고 방지(v2.026). */
  closeOnBackdrop?: boolean;
  /**
   * ESC 로 닫기 — **보기만 하는 창만** 켠다 (v2.231.0 · 기본 꺼짐). 입력칸이 있는 창은 켜지 말 것(check:modal 이 잡는다).
   */
  closeOnEsc?: boolean;
  /** closeOnEsc 일 때 ESC 에 할 일 — 기본은 onClose (메모 적다 만 글 확인 같은 가드가 필요하면) */
  onEsc?: () => void;
  dragHandleSelector?: string;
  /**
   * children 을 감싸는 본문 래퍼 클래스. 기본은 단순 스크롤(`flex-1 overflow-y-auto`).
   * 내부가 헤더/본문/푸터로 나뉘어 본문만 스크롤하는 flex-col 모달은
   * `flex-1 min-h-0 overflow-hidden flex flex-col` 으로 넘기고 children 을 그 flex 자식으로 둔다.
   */
  bodyClassName?: string;
}) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const small = vw < 640;
  const w0 = Math.min(initialWidth, vw - 24);
  const h0 = Math.min(initialHeight ?? Math.round(vh * 0.85), vh - 24);
  const [size, setSize] = useState({ w: w0, h: h0 });
  const [pos, setPos] = useState({
    x: initialX !== undefined ? Math.max(12, Math.min(initialX, vw - w0 - 12)) : Math.max(12, Math.round((vw - w0) / 2)),
    y: initialY !== undefined ? Math.max(12, Math.min(initialY, vh - h0 - 12)) : Math.max(12, Math.round((vh - h0) / 2)),
  });
  const drag = useRef<
    { mode: 'move' | 'resize'; sx: number; sy: number; px: number; py: number; pw: number; ph: number } | null
  >(null);

  // 창 쌓임 — 보기 창은 ESC 로 닫고, 입력 창은 ESC 를 받아 삼킨다(아래 창으로 새지 않게) (v2.231.0)
  useDialogLayer(true, closeOnEsc ? (onEsc ?? onClose) : null);

  // 드래그/리사이즈 추적 (window 레벨 — 포인터가 카드를 벗어나도 유지)
  useEffect(() => {
    if (small) return;
    const onMove = (e: globalThis.PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const dx = e.clientX - d.sx;
      const dy = e.clientY - d.sy;
      if (d.mode === 'move') {
        let x = d.px + dx;
        let y = d.py + dy;
        // 카드가 화면 밖으로 완전히 사라지지 않게 (최소 120px 가로 / 44px 세로는 항상 보이게)
        x = Math.min(Math.max(x, 120 - d.pw), window.innerWidth - 120);
        y = Math.min(Math.max(y, 0), window.innerHeight - 44);
        setPos({ x, y });
      } else {
        const w = Math.min(Math.max(d.pw + dx, minWidth), window.innerWidth - d.px - 8);
        const h = Math.min(Math.max(d.ph + dy, minHeight), window.innerHeight - d.py - 8);
        setSize({ w, h });
      }
    };
    const onUp = () => {
      drag.current = null;
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [small, minWidth, minHeight]);

  /**
   * 배경 클릭으로 닫기 — ⚠`onClick` 이 아니라 `onMouseDown` + 타깃 일치로 판정한다 (2026-07-29).
   *   click 은 mousedown·mouseup 의 **공통 조상**에서 발생하므로, 칸 안에서 글자를 드래그하다
   *   창 밖에서 손을 떼면 배경의 click 이 터져 창이 닫히고 입력이 통째로 사라진다(v2.020 과 같은 사고).
   */
  const onBackdropMouseDown = closeOnBackdrop
    ? (e: ReactMouseEvent) => { if (e.target === e.currentTarget) onClose(); }
    : undefined;

  // 작은 화면: 기존 동작(전체 중앙, 드래그/리사이즈 없음)
  if (small) {
    return (
      <div
        className="fixed inset-0 bg-black/40 flex items-start justify-center overflow-y-auto py-6"
        style={{ zIndex }}
        onMouseDown={onBackdropMouseDown}
      >
        <div className="bg-white rounded shadow-xl w-[95vw] max-h-[92vh] overflow-y-auto">
          {children}
        </div>
      </div>
    );
  }

  const onCardPointerDown = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    const t = e.target as HTMLElement;
    // 버튼·입력 등 인터랙티브 요소에선 드래그 시작 안 함
    if (t.closest('button, a, input, select, textarea, [role="button"], [contenteditable]')) return;
    // 드래그 핸들(헤더) 영역에서만 이동 시작
    if (!t.closest(dragHandleSelector)) return;
    drag.current = { mode: 'move', sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y, pw: size.w, ph: size.h };
    document.body.style.userSelect = 'none';
  };
  const onResizePointerDown = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    drag.current = { mode: 'resize', sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y, pw: size.w, ph: size.h };
    document.body.style.userSelect = 'none';
  };

  // 창 본체 — 배경이 있으면 막 안에서 absolute, 없으면 화면에 fixed(뒤 화면은 그대로 눌리고 스크롤된다)
  const card = (
    <div
      className={`${backdrop ? 'absolute shadow-xl' : 'fixed shadow-2xl ring-1 ring-slate-300'} bg-white rounded flex flex-col overflow-hidden`}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h, ...(backdrop ? {} : { zIndex }) }}
      onPointerDown={onCardPointerDown}
    >
      <div className={bodyClassName}>{children}</div>
      {/* 우하단 리사이즈 핸들 */}
      <div
        onPointerDown={onResizePointerDown}
        title="크기 조절 (드래그)"
        className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize z-10"
        style={{
          background:
            'linear-gradient(135deg, transparent 0 50%, #cbd5e1 50% 60%, transparent 60% 72%, #cbd5e1 72% 82%, transparent 82%)',
        }}
      />
    </div>
  );
  if (!backdrop) return card;
  return (
    <div className="fixed inset-0 bg-black/40" style={{ zIndex }} onMouseDown={onBackdropMouseDown}>
      {card}
    </div>
  );
}
